import moment from 'moment';

import { formatLocaleDate } from './formatLocaleDate';
import AttributeRelyingPartyRequest from './interfaces/AttributeRelyingPartyRequest';
import { AsfStatusType } from './interfaces/attributeStatuses';

export enum RelyingPartyRequestStatusType {
    new = 'new',
    processing = 'processing',
    complete = 'complete',
    timeout = 'timeout',
    canceled = 'canceled',
    maxAttemptsExceeded = 'attempts_exceeded',
}

export interface IdoRequestStatus {
    type: RelyingPartyRequestStatusType;
    requestedAt: string | null;
    submittedAt: string | null;
    completedAt: string | null;
    daysProcessing: number;
}

// TODO(PRODUCT-9194): Handle max_attempts_exceeded when it will be available
export function getRpRequestStatusType(rpRequest: AttributeRelyingPartyRequest): RelyingPartyRequestStatusType {
    if (rpRequest.status === AsfStatusType.complete) {
        return RelyingPartyRequestStatusType.complete;
    } else if (rpRequest.status === AsfStatusType.timeout) {
        // TODO(PRODUCT-9195): Consume 'canceled' status when it will be available
        return rpRequest.submitted_at ? RelyingPartyRequestStatusType.canceled : RelyingPartyRequestStatusType.timeout;
    } else if (rpRequest.status === AsfStatusType.terminated) {
        return RelyingPartyRequestStatusType.canceled;
    } else if (rpRequest.attribute_sharing_agreements[0].fulfilment.status === AsfStatusType.timeout) {
        return RelyingPartyRequestStatusType.timeout;
    } else if (rpRequest.status === AsfStatusType.new || rpRequest.status === AsfStatusType.pending) {
        return RelyingPartyRequestStatusType.new;
    }

    return RelyingPartyRequestStatusType.processing;
}

function toDate(timestamp: number | undefined): Date | null {
    return timestamp ? new Date(timestamp * 1000) : null;
}

/**
 * @param locale overrides the browser's own, for tests. Production callers omit it.
 */
export default function getIdoRequestStatus(
    rpRequest: AttributeRelyingPartyRequest,
    locale?: string,
): IdoRequestStatus {
    const submittedDate = toDate(rpRequest.submitted_at?.timestamp);
    const requestedDate = toDate(rpRequest.created_at?.timestamp);
    const completedDate = toDate(rpRequest.completed_at?.timestamp);

    // Counted off the timestamps rather than off the formatted strings below. Those are
    // display text now, so re-reading one with a fixed pattern would misread every
    // locale that does not order a date the way the United States does.
    const lastProcessingTime = completedDate ? moment(completedDate) : moment();
    const daysProcessing = submittedDate
        ? lastProcessingTime.startOf('day').diff(moment(submittedDate).startOf('day'), 'days')
        : 0;

    return {
        type: getRpRequestStatusType(rpRequest),
        submittedAt: submittedDate && formatLocaleDate(submittedDate, locale),
        requestedAt: requestedDate && formatLocaleDate(requestedDate, locale),
        completedAt: completedDate && formatLocaleDate(completedDate, locale),
        daysProcessing,
    };
}
